<script setup>
import { useEmailTemplatesStore, useUsersStore } from '@/stores'
import { computed, ref, onMounted } from 'vue'
import { storeToRefs } from 'pinia'
import { useRoute, useRouter } from 'vue-router'

const emailTemplatesStore = useEmailTemplatesStore()
const usersStore = useUsersStore()
const { myUser } = storeToRefs(usersStore)
const route = useRoute()
const router = useRouter()

const templatesPerPage = 10

const selectedTemplate = ref(null)
const searchValue = ref("")
const showingInactive = ref(false)
const filtering = ref(false)
const currentPage = ref(1)
const orderBy = ref("templateName")
const orderDirection = ref("") // "" for ascending, "-" for descending

const isShowingEditDialog = ref(false)
const isShowingNewDialog = ref(false)
const isShowingVersionHistoryDialog = ref(false)
const isConfirmDialogOpen = ref(false)
const isSeedingDialogOpen = ref(false)
const seedingResult = ref(null)
const isEditingNewlyCloned = ref(false)

// Dynamic sort function
const dynamicSort = function(property) {
  var sortOrder = 1
  if (property[0] === "-") {
    sortOrder = -1
    property = property.substr(1)
  }
  
  return function(a, b) {
    let x = a[property] ? a[property] : ""
    let y = b[property] ? b[property] : ""

    if (typeof x === 'string') x = x.toLowerCase()
    if (typeof y === 'string') y = y.toLowerCase()

    var result = (x < y) ? -1 : (x > y) ? 1 : 0
    
    return result * sortOrder
  }
}

// Group color mapping
const groupColors = {
  user: 'blue',
  reservation: 'green',
  userGroup: 'purple',
  restaurant: 'orange',
  system: 'grey',
  verification: 'cyan',
  membership: 'pink',
  partner: 'teal',
}

// Filtered templates
const filteredTemplates = computed(() => {
  filtering.value = true
  let returnArray = emailTemplatesStore.templates.filter(item => {
    // Filter by active status
    if (!showingInactive.value && !item.isActive) {
      return false
    }
    
    return true
  }) || []

  // Process search input
  if (searchValue.value != '' && searchValue.value && returnArray) {
    const searchLower = searchValue.value.toLowerCase()
    returnArray = returnArray.filter(item => {
      return item?.templateName?.toLowerCase()?.includes(searchLower)
        || item?.displayName?.toLowerCase()?.includes(searchLower)
        || item?.description?.toLowerCase()?.includes(searchLower)
        || item?.defaultSubject?.toLowerCase()?.includes(searchLower)
        || item?.dataFieldGroup?.toLowerCase()?.includes(searchLower)
    })
  }

  returnArray.sort(dynamicSort(orderDirection.value + orderBy.value))

  setTimeout(() => {
    filtering.value = false
  }, 100)

  return returnArray
})

// Load templates
const loadTemplates = async () => {
  await emailTemplatesStore.getTemplates(showingInactive.value)
  await emailTemplatesStore.getFieldGroups()
}

// Show edit dialog
const showEditTemplateDialog = async (templateId, isNewlyCloned = false) => {
  isEditingNewlyCloned.value = isNewlyCloned
  await emailTemplatesStore.getTemplateById(templateId)
  isShowingEditDialog.value = true
}

// Show new dialog
const showNewTemplateDialog = () => {
  isShowingNewDialog.value = true
}

// Show version history dialog
const showVersionHistoryDialog = async templateId => {
  await emailTemplatesStore.getTemplateById(templateId)
  await emailTemplatesStore.getVersions(templateId)
  isShowingVersionHistoryDialog.value = true
}

// Show delete confirmation
const showDeleteDialog = async templateId => {
  await emailTemplatesStore.getTemplateById(templateId)
  isConfirmDialogOpen.value = true
}

// Delete template
const deleteTemplate = async templateId => {
  await emailTemplatesStore.deleteTemplate(templateId)
  await loadTemplates()
}

// Restore template
const restoreTemplate = async templateId => {
  await emailTemplatesStore.restoreTemplate(templateId)
  await loadTemplates()
}

// Clone template
const cloneTemplate = async templateId => {
  try {
    const clonedTemplate = await emailTemplatesStore.cloneTemplate(templateId)
    await loadTemplates()

    // Open the edit dialog for the new cloned template with editable templateName
    await showEditTemplateDialog(clonedTemplate._id, true)
  } catch (error) {
    console.error('Error cloning template:', error)
  }
}

// Seed templates from file system
const seedTemplates = async () => {
  try {
    const result = await emailTemplatesStore.seedTemplates()
    seedingResult.value = result
    isSeedingDialogOpen.value = true
    await loadTemplates()
  } catch (error) {
    console.error('Error seeding templates:', error)
  }
}

// Export filtered templates
const exportFilteredTemplates = function() {
  const items = filteredTemplates.value

  let itemWithMostKeys = {}
  for (let item of items) {
    if (Object.keys(item).length > Object.keys(itemWithMostKeys).length) {
      itemWithMostKeys = item
    }
  }

  const replacer = (key, value) => {
    if (value === null || typeof value === "array" || typeof value === "object") {
      return ''
    }
    
    return value
  }

  const header = Object.keys(itemWithMostKeys).filter(k => k !== 'content') // Exclude content from export
  const csv = [
    header.join(','),
    ...items.map(row => header.map(fieldName => JSON.stringify(row[fieldName], replacer)).join(','))
  ].join('\r\n')

  var download = function(content, fileName, mimeType) {
    var a = document.createElement('a')
    mimeType = mimeType || 'application/octet-stream'

    if (navigator.msSaveBlob) {
      navigator.msSaveBlob(new Blob([content], {
        type: mimeType
      }), fileName)
    } else if (URL && 'download' in a) {
      a.href = URL.createObjectURL(new Blob([content], {
        type: mimeType
      }))
      a.setAttribute('download', fileName)
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
    } else {
      location.href = 'data:application/octet-stream,' + encodeURIComponent(content)
    }
  }

  download(csv, "email-templates.csv", 'text/csv;encoding:utf-8')
}

// Format date
const formatDate = dateString => {
  if (!dateString) return 'N/A'
  const date = new Date(dateString)
  
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

// Deep-link support: opening this page with `?templateName=foo` (e.g. from the
// Comms Hub) auto-opens the edit dialog for that template, then strips the
// query so a refresh won't re-open the dialog.
async function openTemplateFromQuery() {
  const templateName = route.query?.templateName
  if (!templateName) return
  const match = emailTemplatesStore.templates.find(t => t.templateName === templateName)
  if (match?._id) {
    await showEditTemplateDialog(match._id)
  } else {
    // Templates list filters out inactive by default — try fetching by name as a fallback.
    try {
      const fetched = await emailTemplatesStore.getTemplateByName(templateName)
      if (fetched?._id) {
        isShowingEditDialog.value = true
      }
    } catch (err) {
      console.error('Could not open template from query:', err)
    }
  }
  router.replace({ query: { ...route.query, templateName: undefined } })
}

onMounted(async () => {
  await loadTemplates()
  await openTemplateFromQuery()
})
</script>

<template>
  <div>
    <!-- Edit Dialog -->
    <AdminEditEmailTemplateDialog
      v-model:is-dialog-visible="isShowingEditDialog"
      :selected-template="emailTemplatesStore.template"
      :allow-template-name-edit="isEditingNewlyCloned"
      @saved="loadTemplates"
    />

    <!-- New Dialog -->
    <AdminNewEmailTemplateDialog
      v-model:is-dialog-visible="isShowingNewDialog"
      @saved="loadTemplates"
    />

    <!-- Version History Dialog -->
    <EmailTemplateVersionHistoryDialog
      v-model:is-dialog-visible="isShowingVersionHistoryDialog"
      :template="emailTemplatesStore.template"
      :versions="emailTemplatesStore.versions"
      @rollback="loadTemplates"
    />

    <!-- Delete Confirmation Dialog -->
    <ConfirmDialog
      v-model:is-dialog-visible="isConfirmDialogOpen"
      :confirmation-msg="`Are you sure you want to deactivate <span class='text-decoration-underline'>${emailTemplatesStore.template?.displayName}</span>? The template can be restored later.`"
      @confirm="deleteTemplate(emailTemplatesStore.template._id)"
    />

    <!-- Seeding Result Dialog -->
    <VDialog
      v-model="isSeedingDialogOpen"
      max-width="500"
    >
      <VCard>
        <VCardTitle>Seeding Complete</VCardTitle>
        <VCardText v-if="seedingResult">
          <p><strong>Created:</strong> {{ seedingResult.created?.length || 0 }} templates</p>
          <p><strong>Skipped:</strong> {{ seedingResult.skipped?.length || 0 }} (already exist)</p>
          <p v-if="seedingResult.errors?.length">
            <strong>Errors:</strong> {{ seedingResult.errors.length }}
          </p>
          <VList
            v-if="seedingResult.created?.length"
            dense
          >
            <VListSubheader>Created Templates</VListSubheader>
            <VListItem
              v-for="name in seedingResult.created"
              :key="name"
            >
              <VListItemTitle>{{ name }}</VListItemTitle>
            </VListItem>
          </VList>
        </VCardText>
        <VCardActions>
          <VSpacer />
          <VBtn
            color="primary"
            @click="isSeedingDialogOpen = false"
          >
            Close
          </VBtn>
        </VCardActions>
      </VCard>
    </VDialog>

    <!-- Back to Restaurant Admin Button -->
    <div
      v-if="myUser?.profileId"
      class="content-wrapper mt-6 mb-n2"
    >
      <div class="back-button-container">
        <RouterLink
          to="/admin/profile"
          class="back-button d-flex align-center cursor-pointer"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path d="m15 18-6-6 6-6" />
          </svg>
          <span class="ml-1">Back to Restaurant Admin</span>
        </RouterLink>
      </div>
    </div>

    <div class="grid-sidebar-left content-wrapper mt-6">
      <!-- Sidebar -->
      <div>
        <VCard class="mb-4 pa-4 pt-8 pb-7">
          <div class="account-card">
            <div class="account-card-content">
              <div class="mb-2">
                Sorting
              </div>
              <VSelect
                v-model="orderBy"
                class="mb-2"
                label="Order By"
                item-title="text"
                item-value="value"
                :items="[
                  { text: 'Template Name', value: 'templateName' },
                  { text: 'Display Name', value: 'displayName' },
                  { text: 'Field Group', value: 'dataFieldGroup' },
                  { text: 'Version', value: 'version' },
                  { text: 'Updated At', value: 'updatedAt' },
                ]"
                required
              />
              <VRadioGroup
                v-model="orderDirection"
                row
              >
                <VRadio
                  label="A-Z / Oldest"
                  value=""
                  density="compact"
                />
                <VRadio
                  label="Z-A / Newest"
                  value="-"
                  density="compact"
                />
              </VRadioGroup>

              <VCheckbox
                v-model="showingInactive"
                label="Show Inactive Templates"
                density="compact"
                @change="loadTemplates"
              />

              <VBtn
                class="account-card-primary-action-button mb-2"
                @click="showNewTemplateDialog"
              >
                <VIcon
                  start
                  size="18"
                  icon="mdi-plus-circle-outline"
                />
                <span>New Template</span>
              </VBtn>

              <VBtn
                class="account-card-primary-action-button"
                variant="outlined"
                @click="seedTemplates"
              >
                <VIcon
                  start
                  size="18"
                  icon="mdi-database-import"
                />
                <span>Seed from Files</span>
              </VBtn>
            </div>
          </div>
        </VCard>
      </div>

      <!-- Main Content -->
      <div>
        <VCard class="pa-10 pt-8">
          <VRow>
            <h3 class="user-dashboard-card-title mb-1">
              Email Templates ({{ filteredTemplates.length }})
            </h3>
            <VSpacer />
            <VBtn
              color="grey-lighten-1"
              icon="mdi-file-export"
              variant="text"
              @click="exportFilteredTemplates"
            />
          </VRow>

          <VTextField
            v-model="searchValue"
            :loading="filtering"
            density="compact"
            variant="solo"
            :label="(filtering ? 'Syncing... ' : 'Search by name, group, etc.')"
            append-inner-icon="mdi-magnify"
            single-line
            hide-details
            class="mb-4"
            clearable
            @keyup="currentPage = 1"
          />

          <div>
            <template v-for="(template, index) in filteredTemplates">
              <VCard
                v-if="((index >= (currentPage * templatesPerPage) - templatesPerPage) && (index < currentPage * templatesPerPage))"
                :key="template._id"
                class="mb-4 template-card"
                :class="{ 'inactive-template': !template.isActive }"
                @click="async () => await showEditTemplateDialog(template._id)"
              >
                <VCardTitle class="d-flex align-center">
                  <span class="template-name">{{ template.displayName }}</span>
                  <VChip
                    :color="groupColors[template.dataFieldGroup] || 'grey'"
                    size="small"
                    class="ml-2"
                  >
                    {{ template.dataFieldGroup }}
                  </VChip>
                  <VChip
                    v-if="!template.isActive"
                    color="error"
                    size="small"
                    class="ml-2"
                  >
                    Inactive
                  </VChip>
                  <VSpacer />
                  <span class="text-caption text-grey">v{{ template.version }}</span>
                </VCardTitle>

                <VMenu>
                  <template #activator="{ props }">
                    <VBtn
                      density="compact"
                      class="admin-member-menu-button"
                      icon="mdi-dots-vertical"
                      v-bind="props"
                    />
                  </template>

                  <VList>
                    <VListItem @click="showEditTemplateDialog(template._id)">
                      <VListItemTitle>Edit</VListItemTitle>
                    </VListItem>
                    <VListItem @click="cloneTemplate(template._id)">
                      <VListItemTitle>Clone</VListItemTitle>
                    </VListItem>
                    <VListItem @click="showVersionHistoryDialog(template._id)">
                      <VListItemTitle>Version History</VListItemTitle>
                    </VListItem>
                    <VListItem
                      v-if="template.isActive"
                      @click="showDeleteDialog(template._id)"
                    >
                      <VListItemTitle>Deactivate</VListItemTitle>
                    </VListItem>
                    <VListItem
                      v-else
                      @click="restoreTemplate(template._id)"
                    >
                      <VListItemTitle>Restore</VListItemTitle>
                    </VListItem>
                  </VList>
                </VMenu>

                <VCardItem class="py-0">
                  <p class="text-caption text-grey mb-1">
                    <strong>Template Name:</strong> {{ template.templateName }}
                  </p>
                  <p
                    v-if="template.defaultSubject"
                    class="text-caption text-grey mb-1"
                  >
                    <strong>Subject:</strong> {{ template.defaultSubject }}
                  </p>
                  <p
                    v-if="template.description"
                    class="text-caption text-grey mb-1"
                  >
                    {{ template.description }}
                  </p>
                  <p class="text-caption text-grey">
                    <strong>Updated:</strong> {{ formatDate(template.updatedAt) }}
                  </p>
                </VCardItem>
              </VCard>
            </template>

            <VPagination
              v-model="currentPage"
              :length="Math.ceil(filteredTemplates.length / templatesPerPage)"
            />
          </div>
        </VCard>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.template-card {
  cursor: pointer;
  transition: box-shadow 0.2s;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }
}

.inactive-template {
  opacity: 0.6;
  background-color: var(--ds-surface-subtle);
}

.template-name {
  font-weight: 600;
}

.admin-member-menu-button {
  position: absolute;
  float: right;
  top: 0;
  right: 0;
  margin: 20px;
}

.back-button-container {
  padding: 0 4px;
}

.back-button {
  padding: 8px 12px;
  color: rgb(51, 51, 51);
  text-decoration: none;
  font-size: 14px;
  transition: background-color 0.2s;
  border-radius: 4px;
  display: inline-flex;
}

.back-button:hover {
  background-color: rgba(0, 0, 0, 0.04);
}

.back-button svg {
  color: rgb(102, 102, 102);
}
</style>

<route lang="yaml">
meta:
  layout: website
  action: read
  subject: AclDemo
  allowLoggedInOnly: true
</route>
